import { memo } from 'react';
import { Skeleton as MantineSkeleton, Group, Stack, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { LoadingSkeletonProps } from '../../../types/loading';
import classes from './LoadingSkeleton.module.css';

/**
 * Unified Loading Skeleton Component
 *
 * Provides consistent skeleton loading states across the application with:
 * - Multiple skeleton variants (text, rectangular, circular)
 * - Configurable animations and dimensions
 * - Multiple skeleton count support
 * - Mantine 8.x theme integration
 * - Accessibility attributes
 */
export const LoadingSkeleton = memo<LoadingSkeletonProps>(
  function LoadingSkeleton({
    width,
    height,
    variant = 'text',
    animation = 'pulse',
    count = 1,
    className,
    ...props
  }) {
    const { t } = useTranslation();
    // Map animation to Mantine's animate prop
    const animate = animation !== 'none';

    // Map variant to Mantine's circle prop
    const circle = variant === 'circular';

    // Set default height based on variant if not provided
    const getDefaultHeight = () => {
      switch (variant) {
        case 'text':
          return '1em';
        case 'circular':
          return width || '40px';
        case 'rectangular':
        default:
          return '100px';
      }
    };

    const skeletonHeight = height ?? getDefaultHeight();

    // Determine radius based on variant
    const getRadius = () => {
      switch (variant) {
        case 'rectangular':
          return 'md';
        case 'circular':
          return '50%';
        case 'text':
        default:
          return 'sm';
      }
    };

    const containerClass = [classes.container, className]
      .filter(Boolean)
      .join(' ');

    // Single skeleton
    if (count === 1) {
      return (
        <MantineSkeleton
          width={width}
          height={skeletonHeight}
          radius={getRadius()}
          className={containerClass}
          circle={circle}
          animate={animate}
          aria-label={t('common.loading.content')}
          {...props}
        />
      );
    }

    // Multiple skeletons - arrange based on variant
    const skeletons = Array.from({ length: count }).map((_, index) => (
      <MantineSkeleton
        key={`skeleton-${index}`}
        width={width}
        height={skeletonHeight}
        radius={getRadius()}
        circle={circle}
        animate={animate}
        aria-label={t('common.loading.item', { index: index + 1 })}
        {...props}
      />
    ));

    // Stack multiple text/rectangular skeletons vertically
    if (variant === 'text' || variant === 'rectangular') {
      return (
        <Stack gap="xs" className={containerClass}>
          {skeletons}
        </Stack>
      );
    }

    // Group circular skeletons horizontally
    return (
      <Group gap="sm" className={containerClass}>
        {skeletons}
      </Group>
    );
  }
);

LoadingSkeleton.displayName = 'LoadingSkeleton';

/**
 * Specialized Task Card Skeleton
 *
 * Pre-configured skeleton for task cards with appropriate layout
 */
export const TaskCardSkeleton = memo(function TaskCardSkeleton({
  className,
  ...props
}: { className?: string } & Omit<LoadingSkeletonProps, 'variant' | 'count'>) {
  const { t } = useTranslation();
  const containerClass = [classes.taskCardContainer, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Stack
      gap="md"
      p="md"
      className={containerClass}
      role="progressbar"
      aria-label={t('common.loading.taskCard')}
    >
      {/* Header with avatar and title */}
      <Group gap="sm" align="flex-start">
        <LoadingSkeleton variant="circular" width={32} height={32} {...props} />
        <Stack gap="xs" className={classes.taskCardContent}>
          <LoadingSkeleton variant="text" width="60%" height={16} {...props} />
          <LoadingSkeleton
            variant="circular"
            width={12}
            height={12}
            {...props}
          />
        </Stack>
      </Group>

      {/* Content area */}
      <Stack gap="xs" ml="md">
        <LoadingSkeleton variant="text" width="80%" height={12} {...props} />
        <LoadingSkeleton variant="text" width="40%" height={12} {...props} />
        <LoadingSkeleton variant="text" width="60%" height={12} {...props} />
      </Stack>
    </Stack>
  );
});

/**
 * Specialized List Item Skeleton
 *
 * Pre-configured skeleton for list items
 */
export const ListItemSkeleton = memo(function ListItemSkeleton({
  className,
  showAvatar = true,
  ...props
}: {
  className?: string;
  showAvatar?: boolean;
} & Omit<LoadingSkeletonProps, 'variant' | 'count'>) {
  const { t } = useTranslation();
  const containerClass = [classes.listItemContainer, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Group
      gap="sm"
      p="sm"
      className={containerClass}
      role="progressbar"
      aria-label={t('common.loading.listItem')}
    >
      {showAvatar && (
        <LoadingSkeleton variant="circular" width={24} height={24} {...props} />
      )}
      <Box className={classes.listItemContent}>
        <LoadingSkeleton variant="text" width="70%" height={14} {...props} />
        <LoadingSkeleton variant="text" width="40%" height={12} {...props} />
      </Box>
    </Group>
  );
});

// Export props interface for convenience
export type { LoadingSkeletonProps };
